import { Badge, Box, Button, Flex, Stack, Text, useDisclosure, Wrap, WrapItem } from "@chakra-ui/react";
import { Page } from "../../components/Page";
import PageHeader from "../../components/text/PageHeader";
import { useGetAllOrganizations } from "../../hooks/query/esg-admin/esg-admin";
import React, { useMemo } from "react";
import LocalSpinner from "../../components/LocalSpinner";
import ModuleListPopup from "./ModuleListPopup";
import FrameworkTagsPopup from "./FrameworkTagsPopup";
import CreateOrgPopup from "./CreateOrgPopup";
import OrganizationsTable from "./OrganizationsTable";
import EditOrganizationModal from "./EditOrganizationModal";
import AiModelsPopup from "./AiModelsPopup";
import { formatAnnualLimitGbp, getAiModelsAllocation } from "./aiModelsAllocation";
import MODULES from "config/modules";

const OrganizationList = () => {
  const { isOpen: isModuleListOpen, onOpen: onModuleListOpen, onClose: onModuleListClose } = useDisclosure();
  const { isOpen: isTagsOpen, onOpen: onTagsOpen, onClose: onTagsClose } = useDisclosure();
  const { isOpen: isEditOrgOpen, onOpen: onEditOrgOpen, onClose: onEditOrgClose } = useDisclosure();
  const { isOpen: isCreateOrgOpen, onOpen: onCreateOrgOpen, onClose: onCreateOrgClose } = useDisclosure();
  const { isOpen: isAiModelsOpen, onOpen: onAiModelsOpen, onClose: onAiModelsClose } = useDisclosure();
  const [selectedOrg, setSelectedOrg] = React.useState(null);
  // AI models allocation is frontend-only/mock (see aiModelsAllocation.js) — bump this
  // to force the AI Models column chip to re-read the store after a save, since it's
  // not backed by react-query.
  const [aiModelsVersion, setAiModelsVersion] = React.useState(0);

  const { data: orgList, isLoading } = useGetAllOrganizations();

  const columns = useMemo(() => {
    const handleRowClick = (row) => {
      setSelectedOrg(row.original);
      onEditOrgOpen();
    };

    const handleClickConfigure = (org) => {
      setSelectedOrg(org);
      onModuleListOpen();
    };

    const handleClickTags = (org) => {
      setSelectedOrg(org);
      onTagsOpen();
    };

    const handleClickAiModels = (org) => {
      setSelectedOrg(org);
      onAiModelsOpen();
    };

    return [
      {
        header: "Name",
        accessorKey: "name",
        size: 180,
        meta: { clickable: true },
        cell: ({ row }) => (
          <Box
            cursor="pointer"
            onClick={() => handleRowClick(row)}
            _hover={{ color: "limeShade.600" }}
          >
            {row.original.name}
          </Box>
        ),
      },
      {
        header: "Tenant ID",
        accessorKey: "tenantId",
        meta: { clickable: true },
        cell: ({ row, cell }) => (
          <Box
            cursor="pointer"
            onClick={() => handleRowClick(row)}
            _hover={{ color: "limeShade.600" }}
          >
            <Text fontSize="xs" color="gray.500" fontFamily="mono">{cell.getValue()}</Text>
          </Box>
        ),
      },
      {
        header: "Modules",
        accessorKey: "modules",
        cell: ({ row }) => (
          <Box
            cursor="pointer"
            onClick={(e) => { e.stopPropagation(); handleClickConfigure(row.original); }}
            _hover={{ opacity: 0.7 }}
          >
            <Wrap spacing={1}>
              {!row.original?.modules?.length || row.original.modules.length >= Object.keys(MODULES).length ? (
                <WrapItem>
                  <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme="gray">All modules</Badge>
                </WrapItem>
              ) : row.original.modules.map((module) => (
                <WrapItem key={module}>
                  <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme="blue" whiteSpace="normal" lineHeight="1.4">
                    {module}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        ),
      },
      {
        header: "Report Frameworks",
        accessorKey: "accessTags",
        cell: ({ row }) => (
          <Box
            cursor="pointer"
            onClick={(e) => { e.stopPropagation(); handleClickTags(row.original); }}
            _hover={{ opacity: 0.7 }}
          >
            <Wrap spacing={1}>
              {row.original?.accessTags?.length > 0 ? (
                row.original.accessTags.map((tag) => (
                  <WrapItem key={tag}>
                    <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme="green" whiteSpace="normal" lineHeight="1.4">
                      {tag}
                    </Badge>
                  </WrapItem>
                ))
              ) : (
                <WrapItem>
                  <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme="gray">All frameworks</Badge>
                </WrapItem>
              )}
            </Wrap>
          </Box>
        ),
      },
      {
        header: "AI Models",
        id: "aiModels",
        cell: ({ row }) => {
          // eslint-disable-next-line no-unused-vars -- read for its side effect: re-render when aiModelsVersion bumps
          const _version = aiModelsVersion;
          const allocation = getAiModelsAllocation(row.original.tenantId);
          return (
            <Box
              cursor="pointer"
              onClick={(e) => { e.stopPropagation(); handleClickAiModels(row.original); }}
              _hover={{ opacity: 0.7 }}
            >
              <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme={allocation.annualLimitGbp ? "purple" : "gray"}>
                {formatAnnualLimitGbp(allocation.annualLimitGbp)}
              </Badge>
            </Box>
          );
        },
      },
    ];
  }, [onModuleListOpen, onTagsOpen, onEditOrgOpen, onAiModelsOpen, aiModelsVersion]);

  if (isLoading) {
    return <LocalSpinner />;
  }

  return (
    <Page>
      <Stack spacing={10} p={4} bg="pureWhite" marginLeft="15%" marginRight="15%">
        <Flex justify="space-between" align="flex-start">
          <PageHeader
            title="Organizations"
            subTitle="Manage organizations, their module access, and framework tags across the platform."
          />
          <Button variant="primary" borderRadius="full" onClick={() => onCreateOrgOpen()} flexShrink={0} mt={2}>
            + Create Organization
          </Button>
        </Flex>

        <ModuleListPopup organization={selectedOrg} isOpen={isModuleListOpen} onClose={onModuleListClose} />
        <FrameworkTagsPopup organization={selectedOrg} isOpen={isTagsOpen} onClose={onTagsClose} />
        <EditOrganizationModal organization={selectedOrg} isOpen={isEditOrgOpen} onClose={onEditOrgClose} />
        <CreateOrgPopup isOpen={isCreateOrgOpen} onClose={onCreateOrgClose} />
        <AiModelsPopup
          organization={selectedOrg}
          isOpen={isAiModelsOpen}
          onClose={onAiModelsClose}
          onSaved={() => setAiModelsVersion((v) => v + 1)}
        />

        <OrganizationsTable
          columns={columns}
          data={orgList}
          isLoading={isLoading}
        />
      </Stack>
    </Page>
  );
};

export default OrganizationList;
